import { useCallback, useEffect, useMemo, useState } from "react";
import { getPersonnel, removePersonnel, type PersonnelItem } from "../../../api/etablissement";
import { useAuth } from "../../../store/authEtablissementStore";
import { showToast } from "../../../components/ui/toastHotSonner";
import DemandesEntree from "./DemandesEntree";

const PAGE_SIZE = 20;

const PERSONNEL_TABS = [
  { key: "personnel", label: "Mon personnel" },
  { key: "demandes", label: "Demandes d'entrée" },
] as const;

type PersonnelTab = (typeof PERSONNEL_TABS)[number]["key"];

function formatDateFR(input?: string | null): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleDateString("fr-FR");
}

export default function EtablissementPersonnel() {
  const { establishment } = useAuth();
  const etablissementId = useMemo(() => establishment?.id?.trim() ?? "", [establishment?.id]);

  const [tab, setTab] = useState<PersonnelTab>("personnel");
  const [items, setItems] = useState<PersonnelItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PersonnelItem | null>(null);
  const [removeReason, setRemoveReason] = useState("");
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!etablissementId) {
      setLoading(false);
      setError("Identifiant établissement indisponible.");
      return;
    }
    setLoading(true);
    setError(null);
    const res = await getPersonnel(etablissementId, page, PAGE_SIZE);
    if (!res.ok) {
      setError(res.message);
      setLoading(false);
      return;
    }
    setItems(res.data);
    setTotalPages(res.pagination.totalPages || 1);
    setLoading(false);
  }, [etablissementId, page]);

  useEffect(() => { void load(); }, [load]);

  const confirmRemove = async () => {
    if (!removeTarget || !etablissementId) return;
    const motif = removeReason.trim();
    if (motif.length < 5) {
      setRemoveError("Le motif doit comporter au moins 5 caractères.");
      return;
    }
    setRemoving(true);
    setRemoveError(null);
    const res = await removePersonnel(etablissementId, removeTarget.liaison.id, motif);
    setRemoving(false);
    if (!res.ok) {
      if (res.status === 422) setRemoveError(res.message);
      else if (res.status === 403) showToast("Cette liaison n'appartient pas à votre établissement.", "error");
      else if (res.status === 404) showToast("Ce professionnel a déjà été retiré ou n'existe plus.", "error");
      else showToast(res.message, "error");
      return;
    }
    const target = removeTarget;
    setRemoveTarget(null);
    setRemoveReason("");
    setItems((prev) => prev.filter((i) => i.liaison.id !== target.liaison.id));
    showToast("Professionnel retiré de votre personnel.", "success");
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold">Mon personnel</h1>
      <p className="text-sm text-gray-600">
        Liste auto-déclarative : ces professionnels se sont déclarés eux-mêmes affiliés à votre établissement (ou y ont
        réalisé une consultation), après validation de votre part pour les nouvelles demandes.
      </p>

      <div className="flex flex-wrap gap-2">
        {PERSONNEL_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className="h-10 rounded-[10px] px-4 text-sm font-medium transition-colors"
            style={{ background: tab === t.key ? "#00553E" : "#E5E7EB", color: tab === t.key ? "#fff" : "#0B1F16" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "demandes" && (
        <DemandesEntree etablissementId={etablissementId} onApproved={() => { setPage(1); void load(); }} />
      )}

      {tab === "personnel" && (
      <>
      {loading && <div className="py-12 text-center text-gray-600">Chargement…</div>}

      {!loading && error && (
        <div className="rounded-[10px] border p-6 text-center" style={{ borderColor: "#E5E7EB" }}>
          <p className="text-gray-700">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 h-10 px-4 rounded-[10px] font-medium"
            style={{ backgroundColor: "#00553E", color: "#fff" }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-[10px] border" style={{ borderColor: "#E5E7EB" }}>
          <div className="hidden md:grid md:grid-cols-12 gap-x-6 px-6 py-4 font-semibold text-gray-700">
            <div className="md:col-span-3">Professionnel</div>
            <div className="md:col-span-2">Spécialité</div>
            <div className="md:col-span-2">Poste déclaré</div>
            <div className="md:col-span-2">Depuis</div>
            <div className="md:col-span-3 text-right">Actions</div>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {items.length === 0 ? (
              <div className="text-gray-500 px-3 py-6 text-center">Aucun professionnel déclaré pour le moment.</div>
            ) : (
              items.map((item) => (
                <div
                  key={item.liaison.id}
                  className="bg-gray-100 rounded-[10px] p-3 md:px-4 md:py-2 md:min-h-14 flex flex-col gap-2 md:grid md:grid-cols-12 md:gap-x-6 md:items-center"
                >
                  <div className="md:col-span-3">
                    <div className="font-medium text-[#0B1F16]">
                      {item.professionnel.nom} {item.professionnel.prenom}
                    </div>
                    <div className="text-xs text-gray-500">NIP {item.professionnel.nip}</div>
                  </div>
                  <div className="md:col-span-2 text-sm text-gray-700">{item.professionnel.specialite ?? "—"}</div>
                  <div className="md:col-span-2 text-sm text-gray-700">{item.liaison.poste ?? "—"}</div>
                  <div className="md:col-span-2 text-sm text-gray-700">{formatDateFR(item.liaison.dateDebut) || "—"}</div>
                  <div className="md:col-span-3 md:text-right">
                    <button
                      type="button"
                      onClick={() => { setRemoveTarget(item); setRemoveReason(""); setRemoveError(null); }}
                      className="h-9 px-3 rounded-[10px] border text-sm text-red-600"
                      style={{ borderColor: "#F3C0C0" }}
                    >
                      Retirer du personnel
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t text-sm" style={{ borderColor: "#E5E7EB" }}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                ← Précédent
              </button>
              <span className="text-gray-500">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="disabled:opacity-40"
                style={{ color: "#00553E" }}
              >
                Suivant →
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {removeTarget && (
        <div className="fixed inset-0 z-20">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => { if (removing) return; setRemoveTarget(null); setRemoveReason(""); setRemoveError(null); }}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] p-6 w-full max-w-md shadow">
              <div className="text-lg font-semibold text-[#0B1F16]">
                Retirer {removeTarget.professionnel.nom} {removeTarget.professionnel.prenom} de votre personnel ?
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Ce professionnel ne pourra plus créer de consultation pour vos patients tant qu'il n'aura pas redéclaré
                (et été revalidé par) votre établissement.
              </p>

              <div className="mt-4 space-y-1">
                <label className="text-sm font-medium text-[#0B1F16]">
                  Motif du retrait <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500">
                  Ce texte sera transmis au professionnel par notification — pas de jargon interne.
                </p>
                <textarea
                  value={removeReason}
                  onChange={(e) => { setRemoveReason(e.target.value); setRemoveError(null); }}
                  placeholder="Ex. absences répétées non justifiées sur les 3 derniers mois"
                  className="w-full min-h-[80px] rounded-[10px] border px-3 pt-2 text-sm"
                  style={{ borderColor: "#E5E7EB" }}
                  disabled={removing}
                />
              </div>

              {removeError && (
                <div className="mt-2 rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {removeError}
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#B91C1C" }}
                  disabled={removing || removeReason.trim().length < 5}
                  onClick={() => void confirmRemove()}
                >
                  {removing ? "Retrait…" : "Confirmer"}
                </button>
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold border"
                  style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  onClick={() => { setRemoveTarget(null); setRemoveReason(""); setRemoveError(null); }}
                  disabled={removing}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
